import React, { useState, useEffect, useCallback } from 'react';
import { blink } from '@/lib/blink';
import { requestManager } from '@/lib/requestManager';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, Users, TrendingUp, RefreshCw, Mic, Download, Database } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ContactCardFull } from '@/components/contact-intelligence/ContactCardFull';
// NOTE: ContactCardFull is a new component replacing ContactTile to show full editable fields.
import { ContactHoverPanel } from '@/components/contact-intelligence/ContactHoverPanel';
import { ContactDetailDrawerV3 } from '@/components/contact-intelligence/ContactDetailDrawerV3';
import { ContactDetailDrawerEnhanced } from '@/components/contact-intelligence/ContactDetailDrawerEnhanced';
import { DEMO_RELATIONAL_PROFILE, type RelationalProfile } from '@/types/relationalProfile';
import { calculatePAMSSACScoreV2 } from '@/services/pamssacScoringEngineV2';
import { calculateCIScoreV2 } from '@/services/ciScoringEngineV2';
import { batchProcessCIScores } from '@/services/ciBatchProcessor';
import { contactTimelineService, type TimelineEvent } from '@/services/contactTimelineService';
import VoiceJournalModal from '@/components/VoiceJournalModal';
import { useOptimisticTimeline } from '@/hooks/useOptimisticTimeline';
import { journalSyncQueue } from '@/services/journalSyncQueue';
import { safeJsonParse } from '@/lib/utils';

interface Contact {
  id: string; // personId for identification
  dbRecordId?: string; // actual database record ID for updates
  email: string;
  name?: string;
  company?: string;
  aiRelationshipScore: number;
  totalInteractions: number;
  lastInteractionAt?: string;
  relationshipHealth?: string;
  engagementLevel?: string;
}

// Skeleton loader component - defined outside to avoid hook issues
const ContactSkeleton = React.memo(() => (
  <div className="glass-card border-border/50 p-4 rounded-lg space-y-3">
    <div className="flex items-center gap-3">
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-[200px]" />
        <Skeleton className="h-3 w-[150px]" />
      </div>
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
    <Skeleton className="h-20 w-full" />
  </div>
));

ContactSkeleton.displayName = 'ContactSkeleton';

export default function ContactIntelligenceRedesign() {
  console.log('🎬 [LIFECYCLE] ContactIntelligence component function called');
  
  const [user, setUser] = useState<any>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLazyLoading, setIsLazyLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalToLoad, setTotalToLoad] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [hoveredContact, setHoveredContact] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterBy, setFilterBy] = useState<'all' | 'ready' | 'decaying' | 'high-influence'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'heatmap'>('list');
  const [pamssacScores, setPamssacScores] = useState<Record<string, any>>({});
  const [ciScores, setCiScores] = useState<Record<string, any>>({});
  const [baseTimelineEvents, setBaseTimelineEvents] = useState<TimelineEvent[]>([]);
  
  // Optimistic timeline hook for instant UI updates
  const {
    optimisticEvents: timelineEvents,
    addOptimisticEvent,
    confirmEvent: confirmTimelineEvent,
    failEvent: failTimelineEvent,
  } = useOptimisticTimeline(baseTimelineEvents, async (event) => {
    // Retry function for failed syncs
    const token = blink.auth.getToken();
    if (!token) throw new Error('Not authenticated');
    
    const response = await fetch('https://ben3ou87--contact-intelligence.functions.blink.new', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'sync_timeline',
        userId: user?.id,
        personId: event.id?.split('_')[1], // Extract person ID from event
        contactEmail: event.evidenceSnippet, // Store email temporarily
      }),
    });
    
    if (!response.ok) throw new Error('Sync failed');
  });
  const [showVoiceJournal, setShowVoiceJournal] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processingBatch, setProcessingBatch] = useState(false);
  const [evidenceData, setEvidenceData] = useState<Record<string, any[]>>({});
  const [showDemoCard, setShowDemoCard] = useState(true);
  const [demoCardOpen, setDemoCardOpen] = useState(false);
  const [metricsData, setMetricsData] = useState({
    totalContacts: 0,
    readyToMobilize: 0,
    pamssacHigh: 0,
    filteredCount: 0
  });
  const [showEnhancedDrawer, setShowEnhancedDrawer] = useState(false);

  console.log('🪝 [HOOKS] All useState hooks called successfully');
  console.log('📊 [STATE] Current state snapshot:', {
    hasUser: !!user,
    userId: user?.id,
    contactsCount: contacts.length,
    loading,
    isLazyLoading,
    loadedCount,
    totalToLoad,
    searchQuery,
    filterBy,
    metricsData
  });

  // Efficient paginated loading with real lazy loading
  const loadContactData = useCallback(async (userId: string) => {
    console.log('📥 [DATA LOAD] loadContactData function called');
    
    try {
      setLoading(true);
      setIsLazyLoading(false);
      setLoadedCount(0);
      setContacts([]);
    
      console.log('🔍 [TRACE] Loading contacts with pagination for user:', userId);
      
      // Use request manager with caching - fetch first batch only
      const BATCH_SIZE = 20; // Load 20 at a time
      let contactsData: any[] = [];
      let isUnifiedTable = false;
      
      try {
        console.log('🔍 [TRACE] Fetching first batch from unified table');
        const unifiedData = await requestManager.execute(
          `contacts:unified:${userId}`,
          () => blink.db.contactIntelligenceUnified.list({
            where: { 
              userId: userId
            },
            orderBy: { overallScore: 'desc' },
            limit: BATCH_SIZE
          }),
          {
            priority: 10,
            cacheTTL: 120000,
          }
        );
        
        contactsData = Array.isArray(unifiedData) ? unifiedData : [];
        isUnifiedTable = contactsData.length > 0;
        console.log('✅ Unified table query succeeded, got:', contactsData.length, 'contacts');
      } catch (unifiedError) {
        console.warn('⚠️ Unified table unavailable, trying regular contacts table:', unifiedError);
        try {
          const regularData = await requestManager.execute(
            `contacts:regular:${userId}`,
            () => blink.db.contacts.list({
              where: { 
                userId: userId
              },
              orderBy: { aiRelationshipScore: 'desc' },
              limit: BATCH_SIZE
            }),
            {
              priority: 10,
              cacheTTL: 120000,
            }
          );
          
          contactsData = Array.isArray(regularData) ? regularData : [];
          console.log('✅ Regular contacts table query succeeded, got:', contactsData.length, 'contacts');
        } catch (contactsError) {
          console.error('❌ Both table queries failed:', contactsError);
          contactsData = [];
        }
      }

      console.log('📦 Received contacts from ' + (isUnifiedTable ? 'unified' : 'regular') + ' table:', contactsData?.length || 0);

      if (!Array.isArray(contactsData) || contactsData.length === 0) {
        console.log('⚠️ [DATA] No contacts found');
        setContacts([]);
        setMetricsData({
          totalContacts: 0,
          readyToMobilize: 0,
          pamssacHigh: 0,
          filteredCount: 0
        });
        setLoading(false);
        toast.info('No contacts found. Click "Re-Extract" to analyze your emails.');
        return;
      }
      
      console.log('📊 [DATA] Processing batch of', contactsData.length, 'contacts');
      
      // Process entire batch instantly (no delays)
      const mappedContacts: Contact[] = [];
      const scores: Record<string, any> = {};
      let readyCount = 0;
      let pamssacHighCount = 0;
      
      for (const rawContact of contactsData) {
        const relationshipHealthScore = Number(rawContact.relationshipHealth) || 0;
        const engagementLevelScore = Number(rawContact.engagementLevel) || 0;
        const scoreValue = Number(rawContact.overallScore || rawContact.aiRelationshipScore) || 50;
        
        // Use personId for identification, but keep the actual DB record ID separate
        const personId = rawContact.personId || rawContact.id || `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const dbRecordId = rawContact.id; // The actual database record ID for updates
        const email = rawContact.contactEmail || rawContact.email || 'unknown@email.com';
        
        const mappedContact: Contact = {
          id: personId,
          dbRecordId: dbRecordId, // Store the actual DB record ID
          email: email,
          name: rawContact.name || (email?.split('@')?.[0] || 'Unknown'),
          company: rawContact.company || 'N/A',
          aiRelationshipScore: Math.max(0, Math.min(100, scoreValue)),
          totalInteractions: Math.max(0, Number(rawContact.totalInteractions) || 0),
          lastInteractionAt: rawContact.lastInteractionAt || null,
          relationshipHealth: relationshipHealthScore > 70 ? 'excellent' : relationshipHealthScore > 50 ? 'good' : 'fair',
          engagementLevel: engagementLevelScore > 70 ? 'high' : engagementLevelScore > 40 ? 'medium' : 'low',
        };
        
        mappedContacts.push(mappedContact);
        
        // Calculate PAMSSAC scores
        if (rawContact && typeof rawContact === 'object') {
          const overallScore = Number(rawContact.overallScore) || 0;
          const P = Number(rawContact.proximity) || 0;
          const A1 = Number(rawContact.affinity) || 0;
          const M = Number(rawContact.mutuality) || 0;
          const S1 = Number(rawContact.status) || 0;
          const S2 = Number(rawContact.similarity) || 0;
          const A2 = Number(rawContact.availability) || 0;
          const C = Number(rawContact.complementarity) || 0;
          
          const dimensions = [
            { dimension: 'Proximity', score: P, why: 'Low proximity - limited shared context' },
            { dimension: 'Affinity', score: A1, why: 'Low affinity - weak emotional connection' },
            { dimension: 'Mutuality', score: M, why: 'Low mutuality - unbalanced exchange' },
            { dimension: 'Status', score: S1, why: 'Low status match - seniority gap' },
            { dimension: 'Similarity', score: S2, why: 'Low similarity - different backgrounds' },
            { dimension: 'Availability', score: A2, why: 'Low availability - hard to reach' },
            { dimension: 'Complementarity', score: C, why: 'Low complementarity - limited synergy' }
          ].sort((a, b) => a.score - b.score);
          
          scores[mappedContact.id] = {
            P, A1, M, S1, S2, A2, C,
            total: overallScore,
            weakestTwo: dimensions.slice(0, 2),
            evidence: {}
          };
          
          if (overallScore >= 75) {
            readyCount++;
            pamssacHighCount++;
          }
        } else {
          scores[mappedContact.id] = {
            P: 50, A1: 50, M: 50, S1: 50, S2: 50, A2: 50, C: 50,
            total: mappedContact.aiRelationshipScore,
            weakestTwo: [
              { dimension: 'Calculating...', score: 0, why: 'Run "Process CI Scores" to calculate' }
            ],
            evidence: {}
          };
        }
      }
      
      // Set all contacts at once (batch update)
      setContacts(mappedContacts);
      setPamssacScores(scores);
      setLoadedCount(mappedContacts.length);
      setTotalToLoad(mappedContacts.length); // Will be updated when we implement infinite scroll
      
      // Update metrics
      setMetricsData({
        totalContacts: mappedContacts.length,
        readyToMobilize: readyCount,
        pamssacHigh: pamssacHighCount,
        filteredCount: mappedContacts.length
      });
      
      // Auto-select first contact
      if (mappedContacts.length > 0) {
        setSelectedContact(mappedContacts[0]);
      }
      
      setLoading(false);
      console.log('✅ [DATA LOAD] Batch loading complete. Total contacts:', mappedContacts.length);
      toast.success(`Loaded ${mappedContacts.length} contacts successfully`);
    } catch (error: any) {
      console.error('❌ Failed to load contacts:', error);
      toast.error('Failed to load contacts: ' + (error?.message || 'Unknown error'));
      setLoading(false);
    }
  }, []);

  // Auth effect - uses the memoized loadContactData
  useEffect(() => {
    console.log('🔍 [TRACE] Auth effect mounted');
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      console.log('🔍 [TRACE] Auth state changed:', {
        isLoading: state.isLoading,
        isAuthenticated: state.isAuthenticated,
        hasUser: !!state.user,
        userId: state.user?.id,
        userEmail: state.user?.email
      });
      if (state.user) {
        console.log('✅ [AUTH] User authenticated:', {
          userId: state.user.id,
          email: state.user.email,
          displayName: state.user.displayName
        });
        console.log('🔍 [TRACE] User authenticated, calling loadContactData');
        setUser(state.user);
        loadContactData(state.user.id);
      } else if (!state.isLoading) {
        console.log('⚠️ [AUTH] Auth completed with no user - user is not authenticated');
        setUser(null);
        setLoading(false);
      } else {
        console.log('⏳ [AUTH] Auth still loading...');
      }
    });
    return () => {
      console.log('🔍 [TRACE] Auth effect cleanup');
      unsubscribe();
    };
  }, [loadContactData]); // Depend on loadContactData (which is stable due to useCallback)

  // Generate sparkline data (memoized per contact to avoid excessive calls)
  const [sparklineCache] = useState<Record<string, number[]>>({});
  
  const generateSparklineData = useCallback((contactId: string) => {
    if (sparklineCache[contactId]) {
      return sparklineCache[contactId];
    }
    const data = Array.from({ length: 30 }, () => Math.floor(Math.random() * 40 + 60));
    sparklineCache[contactId] = data;
    return data;
  }, [sparklineCache]);

  // Filter contacts based on search and filter criteria
  // ❌ DO NOT UPDATE STATE INSIDE THIS FUNCTION - it causes infinite renders
  const getFilteredContacts = useCallback(() => {
    console.log('🔍 [FILTER] getFilteredContacts called');
    console.log('🔍 [FILTER] Filter parameters:', {
      totalContacts: contacts.length,
      searchQuery,
      filterBy,
      pamssacScoresCount: Object.keys(pamssacScores).length
    });
    
    let filtered = contacts.filter(contact =>
      contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    console.log('🔍 [FILTER] After search filter:', filtered.length, 'contacts');

    switch (filterBy) {
      case 'ready':
        filtered = filtered.filter(c => {
          const pamssac = pamssacScores[c.id];
          return pamssac && pamssac.total >= 75;
        });
        console.log('🔍 [FILTER] After \"ready\" filter:', filtered.length, 'contacts');
        break;
      case 'decaying':
        filtered = filtered.filter(c => {
          // Would check strength delta > 20%
          return c.relationshipHealth === 'poor' || c.relationshipHealth === 'fair';
        });
        console.log('🔍 [FILTER] After \"decaying\" filter:', filtered.length, 'contacts');
        break;
      case 'high-influence':
        filtered = filtered.filter(c => {
          const pamssac = pamssacScores[c.id];
          return pamssac && pamssac.P >= 75 && pamssac.A1 < 50;
        });
        console.log('🔍 [FILTER] After \"high-influence\" filter:', filtered.length, 'contacts');
        break;
      default:
        console.log('🔍 [FILTER] Using \"all\" filter, no additional filtering');
    }

    console.log('✅ [FILTER] Final filtered count:', filtered.length);

    return filtered;
  }, [contacts, searchQuery, filterBy, pamssacScores]);

  const handleContactClick = useCallback((contact: Contact) => {
    console.log('🖱️ [CLICK] Contact clicked:', contact.email, 'ID:', contact.id);
    console.log('🎯 [CLICK] Contact data:', JSON.stringify(contact, null, 2));
    console.log('🔄 [STATE UPDATE] Setting selected contact and opening drawer');

    setSelectedContact(contact);
    setDrawerOpen(true);

    console.log('✅ [CLICK] Drawer state set to true');
    console.log('📊 [CLICK] Selected contact:', contact.name || contact.email);

    if (user) {
      console.log('🔄 [CLICK] User exists, loading additional data for contact:', contact.id);
      console.log('📊 [CLICK] Loading scores, timeline, and evidence');
      loadScoresForContact(user.id, contact.id);
      loadTimelineForContact(user.id, contact.email);
      loadEvidenceForContact(user.id, contact.id, contact.email);
    } else {
      console.warn('⚠️ [CLICK] No user found, skipping data load');
    }
  }, [user]);

  // Calculate filtered contacts using the memoized function
  console.log('🎨 [RENDER] About to call getFilteredContacts()');
  const filteredContacts = getFilteredContacts();
  console.log('✅ [RENDER] getFilteredContacts() completed, got', filteredContacts.length, 'contacts');
  
  // Update filtered count metric in useEffect to avoid state updates during render
  useEffect(() => {
    setMetricsData(prev => {
      // Only update if the value actually changed
      if (prev.filteredCount !== filteredContacts.length) {
        console.log('📊 [METRICS] Updating filtered count:', prev.filteredCount, '→', filteredContacts.length);
        return { ...prev, filteredCount: filteredContacts.length };
      }
      return prev;
    });
  }, [filteredContacts.length]);

  const loadScoresForContact = async (userId: string, contactId: string) => {
    if (pamssacScores[contactId] && ciScores[contactId]) return;

    setLoadingDetails(true);
    try {
      // Load PAMSSAC scores
      if (!pamssacScores[contactId]) {
        const pamssac = await calculatePAMSSACScoreV2(userId, contactId);
        setPamssacScores(prev => ({ ...prev, [contactId]: pamssac }));
      }
      
      // Load CI scores
      if (!ciScores[contactId]) {
        const { scores, card } = await calculateCIScoreV2(userId, contactId);
        setCiScores(prev => ({ 
          ...prev, 
          [contactId]: { scores, card } 
        }));
      }
    } catch (error) {
      console.error('Failed to calculate scores:', error);
      toast.error('Failed to load contact details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadTimelineForContact = async (userId: string, contactEmail: string) => {
    try {
      console.log('🔍 Loading timeline for contact:', contactEmail);
      const events = await contactTimelineService.loadTimelineForContact(userId, contactEmail);
      console.log('✅ Loaded timeline events:', events.length, 'events');
      
      // Transform TimelineEvent[] to the format ContactDetailDrawerV3 expects
      const transformedEvents = events.map((event: any) => ({
        id: event.id,
        date: event.date,
        channel: event.type === 'email' ? 'email' : event.type === 'meeting' ? 'meeting' : 'call',
        topic: event.title,
        sentiment: event.sentiment || 'neutral',
        outcomes: event.metadata?.outcomes || [],
        evidenceSnippet: event.description
      }));
      
      console.log('📊 Transformed timeline for drawer:', transformedEvents.length, 'events');
      setBaseTimelineEvents(transformedEvents);
    } catch (error) {
      console.error('❌ Failed to load timeline:', error);
      toast.error('Failed to load timeline for contact');
    }
  };
  
  // Handle optimistic journal creation
  const handleNoteCreated = (optimisticJournal: {
    id: string;
    userId: string;
    personId: string;
    contactEmail: string;
    title: string;
    content: string;
    insights: string;
    recordedAt: string;
  }) => {
    // Add optimistic event to timeline immediately
    const optimisticEvent: TimelineEvent = {
      id: optimisticJournal.id,
      date: optimisticJournal.recordedAt,
      channel: 'call', // Voice journals show as calls
      topic: optimisticJournal.title,
      sentiment: 'positive',
      outcomes: [optimisticJournal.insights],
      evidenceSnippet: optimisticJournal.content.substring(0, 200),
    };
    
    const optimisticId = addOptimisticEvent(optimisticEvent);
    
    // Reload contact data to get updated counts
    if (user) {
      loadContactData(user.id);
    }
    
    // Reload timeline for this contact to confirm sync
    if (selectedContact) {
      setTimeout(() => {
        loadTimelineForContact(optimisticJournal.userId, optimisticJournal.contactEmail);
      }, 1000);
    }
  };

  const loadEvidenceForContact = async (userId: string, contactId: string, contactEmail: string) => {
    try {
      // Fetch evidence from thread_relational_facts table with request manager
      const evidenceFacts = await requestManager.execute(
        `evidence:facts:${userId}:${contactId}`,
        () => blink.db.threadRelationalFacts.list({
          where: {
            userId: userId,
            personId: contactId
          },
          orderBy: { createdAt: 'desc' },
          limit: 20
        }),
        {
          priority: 3, // Lower priority for detail loads
          cacheTTL: 300000, // Cache for 5 minutes
        }
      );

      console.log('📊 Loaded evidence facts for contact:', contactId, evidenceFacts);

      // Also fetch from messages_enhanced for additional evidence with request manager
      const messages = await requestManager.execute(
        `evidence:messages:${userId}:${contactEmail}`,
        () => blink.db.messagesEnhanced.list({
          where: {
            userId: userId,
            senderEmail: contactEmail
          },
          orderBy: { receivedDate: 'desc' },
          limit: 10
        }),
        {
          priority: 3, // Lower priority for detail loads
          cacheTTL: 300000, // Cache for 5 minutes
        }
      );

      console.log('📧 Loaded messages for evidence:', contactId, messages);

      // Combine into evidence structure
      const evidence: any[] = [];
      
      // Process thread_relational_facts
      if (Array.isArray(evidenceFacts) && evidenceFacts.length > 0) {
        const factsByDimension: Record<string, any[]> = {};
        
        evidenceFacts.forEach((fact: any) => {
          // Extract PAMSSAC-related evidence
          const evidenceStr = fact.evidence || '{}';
          try {
            const evidenceObj = typeof evidenceStr === 'string' ? safeJsonParse(evidenceStr) : evidenceStr;
            
            // Map evidence to PAMSSAC dimensions
            Object.entries(evidenceObj).forEach(([key, value]: [string, any]) => {
              if (!factsByDimension[key]) {
                factsByDimension[key] = [];
              }
              factsByDimension[key].push({
                messageId: fact.threadId || fact.id,
                snippet: Array.isArray(value) ? value.join('; ') : String(value),
                date: fact.createdAt || new Date().toISOString()
              });
            });
          } catch (e) {
            console.warn('Failed to parse evidence:', e);
          }
        });

        // Convert to evidence format
        Object.entries(factsByDimension).forEach(([dimension, citations]) => {
          evidence.push({
            dimension: dimension.toUpperCase(),
            score: 0, // Will be filled from pamssac scores
            citations: citations
          });
        });
      }

      // Add message-based evidence
      if (Array.isArray(messages) && messages.length > 0) {
        const messageEvidence = messages.map((msg: any) => ({
          messageId: msg.providerMessageId || msg.id,
          snippet: msg.snippet || msg.bodyText?.substring(0, 150) || 'No preview available',
          date: msg.receivedDate || msg.createdAt || new Date().toISOString()
        }));

        if (messageEvidence.length > 0) {
          evidence.push({
            dimension: 'Communication History',
            score: 0,
            citations: messageEvidence
          });
        }
      }

      setEvidenceData(prev => ({ ...prev, [contactId]: evidence }));
      return evidence;
    } catch (error) {
      console.error('Failed to load evidence:', error);
      return [];
    }
  };

  const handleExtractContacts = async () => {
    if (!user) return;

    setExtracting(true);
    try {
      const token = blink.auth.getToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('https://ben3ou87--contact-intelligence.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          processHistorical: true,
        }),
      });

      if (!response.ok) throw new Error('Failed to extract contacts');

      const result = await response.json();
      if (result.success) {
        toast.success(`Processed ${result.processedCount} emails! Found ${result.newContactsCount} new contacts.`);
        await loadContactData(user.id);
      }
    } catch (error) {
      console.error('Failed to extract:', error);
      toast.error('Failed to extract contacts');
    } finally {
      setExtracting(false);
    }
  };

  const handleProcessScores = async () => {
    if (!user) return;

    setProcessingBatch(true);
    try {
      toast.info('Processing CI scores for all contacts...');
      
      // Call the CI unified processor
      const token = blink.auth.getToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('https://ben3ou87--ci-unified-processor.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to process CI scores');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Processed ${result.processed} contacts! Top 50: ${result.top50Count}`);
        
        // Reload data
        await loadContactData(user.id);
      }
    } catch (error) {
      console.error('Failed to process scores:', error);
      toast.error('Failed to process CI scores');
    } finally {
      setProcessingBatch(false);
    }
  };

  // ======= ALL HOOKS COMPLETE - SAFE TO DO CONDITIONAL LOGIC =======
  
  console.log('🔍 [RENDER TRACE] ======= RENDER PHASE =======');
  console.log('🔍 [RENDER TRACE] contacts.length =', contacts.length);
  console.log('🔍 [RENDER TRACE] filteredContacts.length =', filteredContacts.length);
  console.log('🔍 [RENDER TRACE] loading =', loading);
  console.log('🔍 [RENDER TRACE] isLazyLoading =', isLazyLoading);
  console.log('🔍 [RENDER TRACE] user =', user ? `${user.email} (${user.id})` : 'NO USER');
  console.log('🔍 [RENDER TRACE] metrics =', JSON.stringify(metricsData, null, 2));
  console.log('🔍 [RENDER TRACE] selectedContact =', selectedContact ? selectedContact.email : 'NONE');
  console.log('🔍 [RENDER TRACE] drawerOpen =', drawerOpen);
  console.log('🔍 [RENDER TRACE] ===========================');

  // Show loading skeleton only during initial load (before user is set or before auth completes)
  // Do NOT require user to be set for the skeleton - just check loading state
  if (loading) {
    console.log('⏳ [RENDER] Showing loading skeleton (loading=true)');
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] to-[#1a1a1b] p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto w-full">
          <div className="space-y-6">
            <Skeleton className="h-12 w-64" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
            <Skeleton className="h-20" />
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <ContactSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If we're past loading but don't have a user, they need to authenticate
  if (!user) {
    console.log('⚠️ [RENDER] No user after loading complete - showing sign-in screen');
    console.log('⚠️ [RENDER] Loading state:', loading, 'User state:', user);
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] to-[#1a1a1b] p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-white">Contact Intelligence</h1>
          <p className="text-muted-foreground">Please sign in to view your contacts</p>
          <Button onClick={() => blink.auth.login()} className="ci-gradient-gold text-[#0a0a0b]">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  console.log('✅ [RENDER] User authenticated, rendering main content for:', user.email);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#0a0a0b] via-[#1a1a1b] to-[#0c0a09] p-4 sm:p-6 lg:p-8 overflow-hidden">
      {/* Desktop layout */}
      <div className="hidden lg:flex h-full gap-6 overflow-hidden">
        <div className="flex-1 space-y-4 flex flex-col overflow-hidden">
          {/* Header - Desktop */}
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text ci-gradient-gold">
              Contact Intelligence
            </h1>
            <p className="text-muted-foreground mt-1 lg:mt-2 font-light tracking-wide text-sm lg:text-base">
              Clean-with-Hidden-Depth • Executive Grade Relationship Intelligence
            </p>
          </div>

          {/* Stats Overview - Desktop */}
          <div className="grid grid-cols-3 gap-3 lg:gap-4">
            <Card className="glass-card border-border/50 ci-glass-shadow glass-card-hover">
              <CardHeader className="pb-2 lg:pb-3">
                <CardTitle className="text-xs lg:text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4 text-[#D4AF37]" />
                  <span className="hidden sm:inline">Total Contacts</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl lg:text-3xl font-bold text-transparent bg-clip-text ci-gradient-gold">
                  {String(metricsData.totalContacts || 0)}
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border/50 ci-glass-shadow glass-card-hover">
              <CardHeader className="pb-2 lg:pb-3">
                <CardTitle className="text-xs lg:text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <TrendingUp className="w-4 h-4 text-[#008080]" />
                  <span className="hidden sm:inline">Ready to Mobilize</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl lg:text-3xl font-bold text-transparent bg-clip-text ci-gradient-teal">
                  {String(metricsData.readyToMobilize || 0)}
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border/50 ci-glass-shadow glass-card-hover">
              <CardHeader className="pb-2 lg:pb-3">
                <CardTitle className="text-xs lg:text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Filter className="w-4 h-4 text-accent" />
                  <span className="hidden sm:inline">Filtered View</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl lg:text-3xl font-bold text-accent">
                  {String(filteredContacts.length || 0)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleExtractContacts}
              variant="outline"
              className="gap-2 glass-card border-border/50 text-xs sm:text-sm"
              disabled={extracting}
            >
              {extracting ? (
                <>
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                  <span className="hidden sm:inline">Extracting...</span>
                </>
              ) : (
                <>
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Re-Extract</span>
                </>
              )}
            </Button>
            <Button
              onClick={handleProcessScores}
              variant="outline"
              className="gap-2 glass-card border-border/50 text-xs sm:text-sm"
              disabled={processingBatch}
            >
              {processingBatch ? (
                <>
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                  <span className="hidden sm:inline">Processing...</span>
                </>
              ) : (
                <>
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Process CI Scores</span>
                </>
              )}
            </Button>
            <Button
              onClick={() => setShowVoiceJournal(true)}
              className="gap-2 ci-gradient-gold text-[#0a0a0b] text-xs sm:text-sm"
            >
              <Mic className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Voice Journal</span>
            </Button>
            <Button
              onClick={() => {
                if (selectedContact) {
                  setShowEnhancedDrawer(true);
                } else {
                  toast.info('Select a contact first');
                }
              }}
              variant="outline"
              className="gap-2 glass-card border-border/50 text-xs sm:text-sm border-primary/50 text-primary"
            >
              <Database className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">View Full Intelligence</span>
            </Button>
            {showDemoCard && (
              <Button
                onClick={() => setDemoCardOpen(true)}
                variant="outline"
                className="gap-2 glass-card border-border/50 text-xs sm:text-sm border-[#D4AF37]/50 text-[#D4AF37]"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">View Demo Contact</span>
              </Button>
            )}
          </div>

          {/* Search and Filter */}
          <Card className="glass-card border-border/50">
            <CardContent className="pt-3 lg:pt-6 space-y-3 lg:space-y-0">
              <div className="flex flex-col lg:flex-row gap-3 lg:gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="contact-search"
                    placeholder="Search contacts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 glass-card border-border/50 text-sm"
                    aria-label="Search contacts by name, email, or company"
                  />
                </div>

                <Select value={filterBy} onValueChange={(v: any) => setFilterBy(v)}>
                  <SelectTrigger className="w-full lg:w-[200px] glass-card border-border/50 text-sm">
                    <SelectValue placeholder="Filter by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Contacts</SelectItem>
                    <SelectItem value="ready">Ready to Mobilize</SelectItem>
                    <SelectItem value="decaying">Decaying Relationships</SelectItem>
                    <SelectItem value="high-influence">High Influence + Low Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Contacts List - Desktop */}
          <Card className="glass-card border-border/50 flex-1 overflow-hidden flex flex-col">
            <CardContent className="p-0 flex-1 overflow-hidden flex flex-col">
              {isLazyLoading && (
                <div className="px-6 py-3 border-b border-border/50 bg-primary/5">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Loading contacts... {String(loadedCount || 0)} / {String(totalToLoad || 0)}</span>
                  </div>
                </div>
              )}
              <ScrollArea className="flex-1 p-4 lg:p-6">
                <div className="space-y-3">
                  {filteredContacts.length === 0 && !isLazyLoading ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="font-medium">No contacts found</p>
                      {contacts.length === 0 ? (
                        <>
                          <p className="text-sm mt-2">Click "Re-Extract" to analyze your emails and discover contacts.</p>
                        </>
                      ) : (
                        <p className="text-sm mt-2">Try adjusting your filters or search terms</p>
                      )}
                    </div>
                  ) : (
                    <>
                      {filteredContacts.map(contact => {
                        const pamssac = pamssacScores[contact.id] || {
                          P: 50, A1: 50, M: 50, S1: 50, S2: 50, A2: 50, C: 50,
                          total: 50,
                          weakestTwo: [{ dimension: 'Loading...', score: 0, why: 'Calculating...' }]
                        };

                        return (
                          <ContactCardFull
                            key={contact.id}
                            id={contact.dbRecordId || contact.id} // Use dbRecordId for updates
                            personId={contact.id}
                            userId={user?.id || ''}
                            contactEmail={contact.email}
                            name={contact.name}
                            company={contact.company}
                            firstInteractionAt={contact.lastInteractionAt}
                            lastInteractionAt={contact.lastInteractionAt}
                            totalInteractions={contact.totalInteractions}
                            overallScore={contact.aiRelationshipScore}
                            scoreTrend={contact.aiRelationshipScore > 70 ? 'up' : contact.aiRelationshipScore < 40 ? 'down' : 'stable'}
                            proximity={pamssac.P}
                            affinity={pamssac.A1}
                            mutuality={pamssac.M}
                            status={pamssac.S1}
                            similarity={pamssac.S2}
                            availability={pamssac.A2}
                            complementarity={pamssac.C}
                            relationshipHealth={Number(contact.relationshipHealth) || 0}
                            engagementLevel={Number(contact.engagementLevel) || 0}
                            isSelected={selectedContact?.id === contact.id}
                            onClick={() => handleContactClick(contact)}
                          />
                        );
                      })}
                      {isLazyLoading && Array.from({ length: Math.min(3, totalToLoad - loadedCount) }).map((_, i) => (
                        <ContactSkeleton key={`skeleton-${i}`} />
                      ))}
                    </>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="lg:hidden space-y-4 h-full flex flex-col overflow-hidden">
        {/* Header - Mobile */}
        <div>
          <h1 className="text-2xl font-bold text-transparent bg-clip-text ci-gradient-gold">
            Contact Intelligence
          </h1>
          <p className="text-muted-foreground mt-1 font-light text-xs">
            Executive Grade Relationship Intelligence
          </p>
        </div>

        {/* Stats Overview - Mobile */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="glass-card border-border/50">
            <CardContent className="p-2">
              <div className="text-lg font-bold text-transparent bg-clip-text ci-gradient-gold">
                {String(metricsData.totalContacts || 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">Contacts</p>
            </CardContent>
          </Card>

          <Card className="glass-card border-border/50">
            <CardContent className="p-2">
              <div className="text-lg font-bold text-transparent bg-clip-text ci-gradient-teal">
                {String(metricsData.readyToMobilize || 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">Ready</p>
            </CardContent>
          </Card>

          <Card className="glass-card border-border/50">
            <CardContent className="p-2">
              <div className="text-lg font-bold text-accent">
                {String(filteredContacts.length || 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">Filtered</p>
            </CardContent>
          </Card>
        </div>

        {/* Search - Mobile */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="contact-search-mobile"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 glass-card border-border/50 text-sm"
            aria-label="Search contacts"
          />
        </div>

        {/* Contacts List - Mobile */}
        <Card className="glass-card border-border/50 flex-1 overflow-hidden flex flex-col">
          <CardContent className="p-0 flex-1 overflow-hidden flex flex-col">
            {isLazyLoading && (
              <div className="px-4 py-2 border-b border-border/50 bg-primary/5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  <span>Loading {String(loadedCount || 0)} / {String(totalToLoad || 0)}</span>
                </div>
              </div>
            )}
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-2">
                {filteredContacts.length === 0 && !isLazyLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm font-medium">No contacts found</p>
                    {contacts.length === 0 ? (
                      <p className="text-xs mt-1">Tap "Re-Extract" to analyze your emails</p>
                    ) : (
                      <p className="text-xs mt-1">Try adjusting filters</p>
                    )}
                  </div>
                ) : (
                  <>
                    {filteredContacts.map(contact => {
                      const pamssac = pamssacScores[contact.id] || {
                        P: 50, A1: 50, M: 50, S1: 50, S2: 50, A2: 50, C: 50,
                        total: 50,
                        weakestTwo: [{ dimension: 'Loading...', score: 0, why: 'Calculating...' }]
                      };
                      const ciTrend = contact.aiRelationshipScore > 70 ? 'up' : contact.aiRelationshipScore < 40 ? 'down' : 'stable';

                      return (
                        <div
                          key={contact.id}
                          onClick={() => handleContactClick(contact)}
                          className="p-3 rounded-lg glass-card border-border/50 cursor-pointer hover:bg-secondary/40 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">{contact.name || contact.email}</p>
                              <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                              <p className="text-xs text-muted-foreground truncate mt-1">{contact.company || 'N/A'}</p>
                            </div>
                            <Badge className="bg-accent/20 text-accent text-xs">
                              {String(contact.aiRelationshipScore || 0)}%
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                    {isLazyLoading && Array.from({ length: Math.min(3, totalToLoad - loadedCount) }).map((_, i) => (
                      <div key={`skeleton-${i}`} className="p-3 rounded-lg glass-card border-border/50 space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Contact Detail Drawer V3 - Main Contact Drawer */}
      {selectedContact && (
        <ContactDetailDrawerV3
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          profile={{
            identity: {
              personId: selectedContact.id,
              canonical: {
                fullName: selectedContact.name || (selectedContact.email?.split('@')?.[0] || 'Unknown'),
                preferredName: selectedContact.name || (selectedContact.email?.split('@')?.[0] || 'Unknown'),
                primaryEmail: selectedContact.email
              },
              contextualTags: [selectedContact.company || 'Professional Contact'].filter(Boolean)
            },
            contact: {
              primaryEmail: selectedContact.email,
              workEmail: selectedContact.email,
              phoneNumbers: [],
              socialProfiles: []
            },
            relationship: {
              summary: `${selectedContact.company || 'Independent'} • ${selectedContact.totalInteractions} interactions`,
              relationshipType: 'professional',
              relationshipStage: pamssacScores[selectedContact.id]?.total >= 75 ? 'engaged' : 'developing',
              yourRole: user?.displayName || 'You',
              theirRole: 'Contact',
              firstInteractionDate: selectedContact.lastInteractionAt || new Date().toISOString(),
              mostRecentInteractionDate: selectedContact.lastInteractionAt || new Date().toISOString(),
              totalTouchpoints: selectedContact.totalInteractions,
              sentimentOverall: selectedContact.aiRelationshipScore >= 70 ? 'positive' : selectedContact.aiRelationshipScore >= 40 ? 'neutral' : 'needs-attention'
            },
            background: {
              currentRole: 'Professional',
              currentOrg: selectedContact.company || 'Independent',
              previousRoles: [],
              education: [],
              skills: [],
              achievements: [],
              publicBio: `Contact from ${selectedContact.company || 'your network'}`
            },
            preferences: {
              communicationPreferences: {
                preferredChannels: ['email'],
                preferredTimes: [],
                responseTimePattern: 'standard',
                communicationStyle: 'professional'
              },
              interests: {
                professionalInterests: [],
                personalInterests: [],
                topicsToAvoid: []
              },
              boundaries: {
                doNotContact: [],
                sensitiveTopics: [],
                privacyPreferences: []
              }
            },
            workStyle: {
              decisionMakingStyle: 'collaborative',
              workingHours: 'standard',
              meetingPreferences: {
                preferredDuration: 30,
                preferredFormat: 'video',
                preferredTimeOfDay: 'morning'
              }
            },
            milestones: {
              relationshipMilestones: [],
              sharedSuccesses: []
            },
            commitments: {
              yourCommitments: [],
              theirCommitments: []
            },
            derivedSignals: {
              computedAt: new Date().toISOString(),
              signalStrength: selectedContact.aiRelationshipScore,
              trends: []
            }
          }}
          ciScores={{
            ci: selectedContact.aiRelationshipScore,
            strength: Math.round(selectedContact.aiRelationshipScore * 0.9),
            relevance: Math.round(selectedContact.aiRelationshipScore * 0.85),
            influence: Math.round(selectedContact.aiRelationshipScore * 0.8),
            responsiveness: Math.round(selectedContact.aiRelationshipScore * 0.75),
            reciprocity: Math.round(selectedContact.aiRelationshipScore * 0.7),
            tier: selectedContact.aiRelationshipScore >= 80 ? 'A' as const : selectedContact.aiRelationshipScore >= 60 ? 'B' as const : 'C' as const,
          }}
          pamssacScores={pamssacScores[selectedContact.id] ? {
            P: pamssacScores[selectedContact.id].P,
            A1: pamssacScores[selectedContact.id].A1,
            M: pamssacScores[selectedContact.id].M,
            S1: pamssacScores[selectedContact.id].S1,
            S2: pamssacScores[selectedContact.id].S2,
            A2: pamssacScores[selectedContact.id].A2,
            C: pamssacScores[selectedContact.id].C,
            total: pamssacScores[selectedContact.id].total,
            weakestTwo: Array.isArray(pamssacScores[selectedContact.id].weakestTwo)
              ? pamssacScores[selectedContact.id].weakestTwo.map((w: any) => ({
                  dimension: typeof w === 'string' ? w : w.dimension || 'Unknown',
                  score: typeof w === 'object' && w.score ? w.score : 0,
                  why: typeof w === 'object' && w.why ? w.why : 'No evidence'
                }))
              : [{ dimension: 'Calculating...', score: 0, why: 'Loading scores' }]
          } : {
            P: 50, A1: 50, M: 50, S1: 50, S2: 50, A2: 50, C: 50,
            total: 50,
            weakestTwo: [
              { dimension: 'Calculating...', score: 0, why: 'Loading scores' }
            ]
          }}
          timeline={timelineEvents}
          decisionContext={{
            owner: user?.displayName || (user?.email?.split('@')?.[0] || 'You'),
            relationshipType: 'Strategic Partnership',
            objective: selectedContact.company ? `Build relationship with ${selectedContact.company}` : 'Strengthen connection',
            valueAtStake: Math.round((pamssacScores[selectedContact.id]?.total || 50) * 1000),
            stage: pamssacScores[selectedContact.id]?.total >= 75 ? 'Ready to Mobilize' : pamssacScores[selectedContact.id]?.total >= 50 ? 'Active Cultivation' : 'Early Stage',
            riskLevel: selectedContact.aiRelationshipScore < 40 ? 'high' as const : selectedContact.aiRelationshipScore < 70 ? 'medium' as const : 'low' as const,
            timeline: selectedContact.lastInteractionAt ? `Last contact: ${new Date(selectedContact.lastInteractionAt).toLocaleDateString()}` : 'No recent contact',
            benchmark: {
              tierMedian: selectedContact.aiRelationshipScore >= 80 ? 85 : selectedContact.aiRelationshipScore >= 60 ? 70 : 55,
              percentile: Math.round((selectedContact.aiRelationshipScore / 100) * 100)
            }
          }}
          evidence={evidenceData[selectedContact.id]?.map((evid: any) => ({
            ...evid,
            score: pamssacScores[selectedContact.id]?.[evid.dimension] || evid.score || 0
          })) || []}
        />
      )}

      {/* Voice Journal Modal */}
      <VoiceJournalModal
        isOpen={showVoiceJournal}
        onClose={() => setShowVoiceJournal(false)}
        userId={user?.id || ''}
        contacts={contacts}
        onNoteCreated={handleNoteCreated}
      />

      {/* Demo Contact Intelligence Drawer */}
      {showDemoCard && (
        <ContactDetailDrawerV3
          open={demoCardOpen}
          onOpenChange={(open) => {
            setDemoCardOpen(open);
            if (!open) {
              // Option to dismiss demo card permanently
              setTimeout(() => {
                toast.info('Demo card removed. You can always access real contact intelligence from the contact list.', {
                  duration: 5000,
                  action: {
                    label: 'Undo',
                    onClick: () => setShowDemoCard(true),
                  },
                });
              }, 500);
              setShowDemoCard(false);
            }
          }}
          profile={{
            identity: {
              personId: selectedContact.id,
              canonical: {
                fullName: selectedContact.name || (selectedContact.email?.split('@')?.[0] || 'Unknown'),
                preferredName: selectedContact.name || (selectedContact.email?.split('@')?.[0] || 'Unknown'),
                primaryEmail: selectedContact.email
              },
              contextualTags: [selectedContact.company || 'Professional Contact'].filter(Boolean)
            },
            contact: {
              primaryEmail: selectedContact.email,
              workEmail: selectedContact.email,
              phoneNumbers: [],
              socialProfiles: []
            },
            relationship: {
              summary: `${selectedContact.company || 'Independent'} • ${selectedContact.totalInteractions} interactions`,
              relationshipType: 'professional',
              relationshipStage: pamssacScores[selectedContact.id]?.total >= 75 ? 'engaged' : 'developing',
              yourRole: user?.displayName || 'You',
              theirRole: 'Contact',
              firstInteractionDate: selectedContact.lastInteractionAt || new Date().toISOString(),
              mostRecentInteractionDate: selectedContact.lastInteractionAt || new Date().toISOString(),
              totalTouchpoints: selectedContact.totalInteractions,
              sentimentOverall: selectedContact.aiRelationshipScore >= 70 ? 'positive' : selectedContact.aiRelationshipScore >= 40 ? 'neutral' : 'needs-attention'
            },
            background: {
              currentRole: 'Professional',
              currentOrg: selectedContact.company || 'Independent',
              previousRoles: [],
              education: [],
              skills: [],
              achievements: [],
              publicBio: `Contact from ${selectedContact.company || 'your network'}`
            },
            preferences: {
              communicationPreferences: {
                preferredChannels: ['email'],
                preferredTimes: [],
                responseTimePattern: 'standard',
                communicationStyle: 'professional'
              },
              interests: {
                professionalInterests: [],
                personalInterests: [],
                topicsToAvoid: []
              },
              boundaries: {
                doNotContact: [],
                sensitiveTopics: [],
                privacyPreferences: []
              }
            },
            workStyle: {
              decisionMakingStyle: 'collaborative',
              workingHours: 'standard',
              meetingPreferences: {
                preferredDuration: 30,
                preferredFormat: 'video',
                preferredTimeOfDay: 'morning'
              }
            },
            milestones: {
              relationshipMilestones: [],
              sharedSuccesses: []
            },
            commitments: {
              yourCommitments: [],
              theirCommitments: []
            },
            derivedSignals: {
              computedAt: new Date().toISOString(),
              signalStrength: selectedContact.aiRelationshipScore,
              trends: []
            }
          }}
          ciScores={{
            ci: selectedContact.aiRelationshipScore,
            strength: Math.round(selectedContact.aiRelationshipScore * 0.9),
            relevance: Math.round(selectedContact.aiRelationshipScore * 0.85),
            influence: Math.round(selectedContact.aiRelationshipScore * 0.8),
            responsiveness: Math.round(selectedContact.aiRelationshipScore * 0.75),
            reciprocity: Math.round(selectedContact.aiRelationshipScore * 0.7),
            tier: selectedContact.aiRelationshipScore >= 80 ? 'A' as const : selectedContact.aiRelationshipScore >= 60 ? 'B' as const : 'C' as const,
          }}
          pamssacScores={pamssacScores[selectedContact.id] ? {
            P: pamssacScores[selectedContact.id].P,
            A1: pamssacScores[selectedContact.id].A1,
            M: pamssacScores[selectedContact.id].M,
            S1: pamssacScores[selectedContact.id].S1,
            S2: pamssacScores[selectedContact.id].S2,
            A2: pamssacScores[selectedContact.id].A2,
            C: pamssacScores[selectedContact.id].C,
            total: pamssacScores[selectedContact.id].total,
            weakestTwo: Array.isArray(pamssacScores[selectedContact.id].weakestTwo)
              ? pamssacScores[selectedContact.id].weakestTwo.map((w: any) => ({
                  dimension: typeof w === 'string' ? w : w.dimension || 'Unknown',
                  score: typeof w === 'object' && w.score ? w.score : 0,
                  why: typeof w === 'object' && w.why ? w.why : 'No evidence'
                }))
              : [{ dimension: 'Calculating...', score: 0, why: 'Loading scores' }]
          } : {
            P: 50, A1: 50, M: 50, S1: 50, S2: 50, A2: 50, C: 50,
            total: 50,
            weakestTwo: [
              { dimension: 'Calculating...', score: 0, why: 'Loading scores' }
            ]
          }}
          timeline={timelineEvents}
          decisionContext={{
            owner: user?.displayName || (user?.email?.split('@')?.[0] || 'You'),
            relationshipType: 'Strategic Partnership',
            objective: selectedContact.company ? `Build relationship with ${selectedContact.company}` : 'Strengthen connection',
            valueAtStake: Math.round((pamssacScores[selectedContact.id]?.total || 50) * 1000),
            stage: pamssacScores[selectedContact.id]?.total >= 75 ? 'Ready to Mobilize' : pamssacScores[selectedContact.id]?.total >= 50 ? 'Active Cultivation' : 'Early Stage',
            riskLevel: selectedContact.aiRelationshipScore < 40 ? 'high' as const : selectedContact.aiRelationshipScore < 70 ? 'medium' as const : 'low' as const,
            timeline: selectedContact.lastInteractionAt ? `Last contact: ${new Date(selectedContact.lastInteractionAt).toLocaleDateString()}` : 'No recent contact',
            benchmark: {
              tierMedian: selectedContact.aiRelationshipScore >= 80 ? 85 : selectedContact.aiRelationshipScore >= 60 ? 70 : 55,
              percentile: Math.round((selectedContact.aiRelationshipScore / 100) * 100)
            }
          }}
          evidence={evidenceData[selectedContact.id]?.map((evid: any) => ({
            ...evid,
            score: pamssacScores[selectedContact.id]?.[evid.dimension] || evid.score || 0
          })) || []}
        />
      )}

      {/* Voice Journal Modal */}
      <VoiceJournalModal
        isOpen={showVoiceJournal}
        onClose={() => setShowVoiceJournal(false)}
        userId={user?.id || ''}
        contacts={contacts}
        onNoteCreated={handleNoteCreated}
      />

      {/* Demo Contact Intelligence Drawer */}
      {showDemoCard && (
        <ContactDetailDrawerV3
          open={demoCardOpen}
          onOpenChange={(open) => {
            setDemoCardOpen(open);
            if (!open) {
              // Option to dismiss demo card permanently
              setTimeout(() => {
                toast.info('Demo card removed. You can always access real contact intelligence from the contact list.', {
                  duration: 5000,
                  action: {
                    label: 'Undo',
                    onClick: () => setShowDemoCard(true),
                  },
                });
              }, 500);
              setShowDemoCard(false);
            }
          }}
          profile={DEMO_RELATIONAL_PROFILE}
          ciScores={{
            ci: 82,
            strength: 85,
            relevance: 78,
            influence: 88,
            responsiveness: 80,
            reciprocity: 75,
            tier: 'A',
          }}
          pamssacScores={{
            P: 85,
            A1: 72,
            M: 90,
            S1: 88,
            S2: 75,
            A2: 78,
            C: 82,
            total: 82,
            weakestTwo: [
              { dimension: 'Access', score: 72, why: 'Primarily email communication, could benefit from more direct channels' },
              { dimension: 'Susceptibility', score: 75, why: 'Strong existing processes, requires clear value demonstration' }
            ]
          }}
          timeline={[
            {
              id: 'demo_timeline_1',
              date: '2025-10-20T14:30:00Z',
              channel: 'email',
              topic: 'Q4 Planning Discussion',
              sentiment: 'positive',
              outcomes: ['Agreed to intro to Ops lead', 'Will review deck by Friday'],
              nextStep: {
                task: 'Send Q4 strategy deck',
                due: '2025-10-22'
              },
              evidenceSnippet: 'Really appreciate the thorough analysis. Let\'s connect our ops teams next week.'
            },
            {
              id: 'demo_timeline_2',
              date: '2025-10-15T10:00:00Z',
              channel: 'meeting',
              topic: 'Product Roadmap Review',
              sentiment: 'positive',
              outcomes: ['Aligned on Q4 priorities', 'Identified 3 key integrations'],
              evidenceSnippet: 'The new workflow automation features align perfectly with our needs.'
            },
            {
              id: 'demo_timeline_3',
              date: '2025-10-10T16:45:00Z',
              channel: 'call',
              topic: 'Voice Journal: Strategic Alignment',
              sentiment: 'positive',
              evidenceSnippet: 'Alex expressed strong interest in deeper partnership. Key concern: scalability for 500+ users.'
            }
          ]}
          decisionContext={{
            owner: 'Sarah Johnson',
            relationshipType: 'Strategic Partnership',
            objective: 'Expand TechCorp engagement to enterprise tier',
            valueAtStake: 250000,
            stage: 'Active Cultivation',
            riskLevel: 'low',
            timeline: 'Last contact: Oct 20, 2025',
            benchmark: {
              tierMedian: 78,
              percentile: 89
            }
          }}
          evidence={[
            {
              dimension: 'Motivation',
              score: 90,
              citations: [
                {
                  messageId: 'msg_001',
                  snippet: 'We need to move fast on this - Q4 is critical for us',
                  date: '2025-10-18'
                },
                {
                  messageId: 'msg_002',
                  snippet: 'I\'ve already got buy-in from the exec team, just need to see the ROI',
                  date: '2025-10-15'
                }
              ]
            },
            {
              dimension: 'Placement',
              score: 85,
              citations: [
                {
                  messageId: 'msg_003',
                  snippet: 'As VP of Engineering, I can approve budget up to $500k without additional sign-off',
                  date: '2025-10-12'
                }
              ]
            }
          ]}
        />
      )}
    </div>
  );
}